import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Egg, Check } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

const todayStr = () => new Date().toISOString().slice(0, 10);

const OvulationMarkerCard = () => {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [date, setDate] = useState(todayStr());
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/wellness/ovulation/history?limit=8`, {
        headers, withCredentials: true,
      });
      setHistory(res.data.dates || []);
    } catch { /* silent */ }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [token]);

  const mark = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/wellness/ovulation/mark?date=${date}`, null, {
        headers, withCredentials: true,
      });
      toast.success("Ovulation marked — predictions will refine 💫");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't mark ovulation");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    try {
      await axios.delete(`${API}/wellness/ovulation/mark?date=${d}`, {
        headers, withCredentials: true,
      });
      toast.success("Removed");
      refresh();
    } catch { toast.error("Couldn't remove"); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.28 }}
      data-testid="ovulation-marker-card"
    >
      <Card className="glass border-emerald-400/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <Egg className="w-5 h-5 text-emerald-300" />
            Mark Ovulation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-mystic-300 text-xs">Confirm your actual ovulation date — boosts cycle prediction accuracy.</p>
          <div className="flex gap-2">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="ovulation-date-input"
              className="flex-1 bg-mystic-800/50 border border-mystic-600 rounded-lg px-3 py-2 text-white text-sm focus:border-mystic-400 outline-none"
            />
            <Button
              onClick={mark}
              disabled={saving}
              data-testid="mark-ovulation-btn"
              className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-full"
            >
              <Check className="w-4 h-4 mr-1.5" />
              {saving ? "Saving..." : "Mark"}
            </Button>
          </div>

          {history.length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <p className="text-xs text-mystic-300 mb-1.5">Recent marks</p>
              <ul className="flex flex-wrap gap-1.5">
                {history.map((d) => (
                  <li
                    key={d}
                    data-testid={`ovulation-mark-${d}`}
                    className="text-xs bg-emerald-500/15 border border-emerald-400/30 text-emerald-100 px-2 py-1 rounded-full flex items-center gap-1"
                  >
                    {d}
                    <button onClick={() => remove(d)} aria-label={`Remove ${d}`} className="text-emerald-100/70 hover:text-rose-300">×</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default OvulationMarkerCard;
